import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "campaignSearch", "adsetSearch",
    "selectedCount", "adsetSelectedCount",
    "campaignsTbody", "adsetsTbody"
  ]

  static values = {
    selectedCampaigns: { type: Array, default: [] },
    selectedAdsets: { type: Array, default: [] }
  }

  connect() {
    // The existing-campaign tables only render in advanced mode — in
    // Minimal mode the controller still connects but has nothing to fill.
    if (!this.hasCampaignsTbodyTarget) return

    // Load campaigns if ad account is already selected (edit mode)
    const adAccountSelect = this.element.querySelector("[name='autoboost[fb_ad_account_id]']")
    if (adAccountSelect?.value) {
      this.fetchCampaigns(adAccountSelect.value)
    }
  }

  // Triggered when ad account dropdown changes
  loadCampaigns(event) {
    if (!this.hasCampaignsTbodyTarget) return

    const adAccountId = event.target.value
    this.clearAdsets()

    if (!adAccountId) {
      this.clearCampaigns()
      return
    }

    this.fetchCampaigns(adAccountId)
  }

  async fetchCampaigns(adAccountId) {
    if (!this.hasCampaignsTbodyTarget) return
    console.log("[campaign-selector] fetchCampaigns:", adAccountId)
    this._currentAdAccountId = adAccountId
    this.showLoading(this.campaignsTbodyTarget)

    try {
      const res = await fetch(`/ad-accounts/${adAccountId}/campaigns.json?per=200`)
      console.log("[campaign-selector] response:", res.status)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      console.log("[campaign-selector] loaded", json.data?.length || 0, "campaigns, syncing:", json.syncing || false)

      if (json.sync_error) {
        console.error("[campaign-selector] sync error from server:", json.sync_error)
      }

      this.renderCampaigns(json.data || [], json.syncing, json.sync_error)
    } catch (e) {
      console.error("[campaign-selector] Failed to load campaigns:", e)
      this.campaignsTbodyTarget.innerHTML = this.messageRow("Failed to load campaigns", 4, true)
    }
  }

  async syncCampaigns() {
    if (!this.hasCampaignsTbodyTarget) return
    const adAccountId = this._currentAdAccountId
    if (!adAccountId) return

    console.log("[campaign-selector] syncCampaigns: manual refresh for", adAccountId)
    this.showLoading(this.campaignsTbodyTarget)

    try {
      const res = await fetch(`/ad-accounts/${adAccountId}/campaigns/sync`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "Accept": "application/json"
        }
      })
      console.log("[campaign-selector] sync response:", res.status)
      const json = await res.json()

      if (json.success) {
        console.log("[campaign-selector] sync complete,", json.data?.length || 0, "campaigns")
        this.renderCampaigns(json.data || [])
      } else {
        console.error("[campaign-selector] sync failed:", json.error)
        this.campaignsTbodyTarget.innerHTML = this.messageRow(`Sync failed: ${json.error}`, 4, true)
      }
    } catch (e) {
      console.error("[campaign-selector] sync request failed:", e)
      this.campaignsTbodyTarget.innerHTML = this.messageRow("Sync request failed", 4, true)
    }
  }

  renderCampaigns(campaigns, syncing = false, syncError = null) {
    if (!campaigns.length) {
      const errorMsg = syncError ? `Sync error: ${syncError}` : (syncing ? "Syncing campaigns from Meta..." : "No campaigns found for this ad account")
      this.campaignsTbodyTarget.innerHTML = this.emptyStateWithRefresh(errorMsg, syncing)
      this.updateCampaignCount()

      // If server indicated it's syncing (first-time fetch returned empty), auto-retry once after a short delay
      if (syncing && !this._retried) {
        this._retried = true
        console.log("[campaign-selector] server syncing, auto-retrying in 3s...")
        setTimeout(() => this.fetchCampaigns(this._currentAdAccountId), 3000)
      }
      return
    }
    this._retried = false

    // Deduplicate by fb_campaign_id
    const seen = new Set()
    campaigns = campaigns.filter(c => {
      if (seen.has(c.fb_campaign_id)) return false
      seen.add(c.fb_campaign_id)
      return true
    })

    const rows = campaigns.map(c => {
      const name = c.name || "Unnamed Campaign"
      const checked = this.selectedCampaignsValue.includes(c.fb_campaign_id) ? "checked" : ""
      const status = this.statusBadge(c.status)
      const objective = c.objective
        ? c.objective.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
        : "\u2014"

      return `<tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
        <td class="px-4 py-3">
          <input type="checkbox" name="autoboost[campaign_ids][]" value="${c.fb_campaign_id}"
                 class="form-checkbox" data-action="campaign-selector#toggleCampaign" ${checked}>
        </td>
        <td class="px-4 py-3">
          <p class="text-sm font-medium text-gray-900 dark:text-white">${this.esc(name)}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">${c.fb_campaign_id}</p>
        </td>
        <td class="px-4 py-3">${status}</td>
        <td class="px-4 py-3">
          <span class="text-sm text-gray-600 dark:text-gray-400">${this.esc(objective)}</span>
        </td>
      </tr>`
    })

    this.campaignsTbodyTarget.innerHTML = rows.join("")
    this.updateCampaignCount()
    this.element.dispatchEvent(new Event("change", { bubbles: true }))

    // Auto-load ad sets if campaigns are pre-selected (edit mode)
    if (this.getSelectedCampaignIds().length > 0) {
      this.loadAdsetsForSelectedCampaigns()
    }
  }

  toggleCampaign() {
    this.updateCampaignCount()
    this.loadAdsetsForSelectedCampaigns()
  }

  toggleAllCampaigns(event) {
    const checked = event.target.checked
    this.campaignsTbodyTarget.querySelectorAll("input[type='checkbox']").forEach(cb => {
      if (!cb.closest("tr").classList.contains("hidden")) cb.checked = checked
    })
    this.updateCampaignCount()
    this.loadAdsetsForSelectedCampaigns()
  }

  filterCampaigns() {
    const query = this.campaignSearchTarget.value.toLowerCase().trim()
    this.campaignsTbodyTarget.querySelectorAll("tr").forEach(row => {
      const name = row.querySelector("p")?.textContent?.toLowerCase() || ""
      row.classList.toggle("hidden", query.length > 0 && !name.includes(query))
    })
  }

  async loadAdsetsForSelectedCampaigns() {
    if (!this.hasCampaignsTbodyTarget || !this.hasAdsetsTbodyTarget) return
    const ids = this.getSelectedCampaignIds()

    if (!ids.length) {
      this.clearAdsets()
      return
    }

    this.showLoading(this.adsetsTbodyTarget)

    try {
      const results = await Promise.all(
        ids.map(id => fetch(`/campaigns/${id}/ad-sets.json?per=200`).then(r => r.json()))
      )
      const all = results.flatMap(r => r.data || [])

      // Deduplicate by fb_ad_set_id
      const seen = new Set()
      const unique = all.filter(a => {
        if (seen.has(a.fb_ad_set_id)) return false
        seen.add(a.fb_ad_set_id)
        return true
      })

      this.renderAdsets(unique)
    } catch (e) {
      console.error("[campaign-selector] Failed to load ad sets:", e)
      this.adsetsTbodyTarget.innerHTML = this.messageRow("Failed to load ad sets", 4, true)
    }
  }

  renderAdsets(adsets) {
    // AB-047c: merge in any saved adset_ids that the newly-selected
    // parent campaign didn't return from the API. Without this, a saved
    // adset silently disappears from the form when the user (or the
    // effective_campaign_ids heal) swaps the parent campaign — user
    // saves, adset gets dropped, orphan state flips direction. Surface
    // it as a visible warning row so the user can keep or explicitly
    // remove it.
    const fetchedIds = new Set((adsets || []).map(a => a.fb_ad_set_id))
    const missingSaved = (this.selectedAdsetsValue || []).filter(id => id && !fetchedIds.has(id))
    if (missingSaved.length) {
      console.warn(`[campaign-selector] ${missingSaved.length} saved adset(s) not returned by API:`, missingSaved)
    }

    if (!adsets.length && !missingSaved.length) {
      this.adsetsTbodyTarget.innerHTML = this.messageRow("No ad sets found for selected campaigns", 4)
      this.updateAdsetCount()
      return
    }

    // Warning rows — saved but missing from Meta sync. Pre-checked so
    // they submit with the form unless the user explicitly unchecks.
    const warningRows = missingSaved.map(id => (
      `<tr class="bg-amber-50 dark:bg-amber-900/10">
        <td class="px-4 py-3">
          <input type="checkbox" name="autoboost[adset_ids][]" value="${id}"
                 class="form-checkbox" data-action="campaign-selector#toggleAdset" checked>
        </td>
        <td class="px-4 py-3">
          <p class="text-sm font-medium text-amber-800 dark:text-amber-200">
            Saved ad set not found in Meta sync
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400">${id}</p>
        </td>
        <td class="px-4 py-3">
          <span class="text-xs text-gray-500 italic">unknown</span>
        </td>
        <td class="px-4 py-3">
          <span class="text-xs text-amber-700 dark:text-amber-300">keep checked to preserve, uncheck to remove</span>
        </td>
      </tr>`
    ))

    const rows = (adsets || []).map(a => {
      const name = a.name || "Unnamed Ad Set"
      const checked = this.selectedAdsetsValue.includes(a.fb_ad_set_id) ? "checked" : ""
      const status = this.statusBadge(a.effective_status || a.configured_status)

      return `<tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
        <td class="px-4 py-3">
          <input type="checkbox" name="autoboost[adset_ids][]" value="${a.fb_ad_set_id}"
                 class="form-checkbox" data-action="campaign-selector#toggleAdset" ${checked}>
        </td>
        <td class="px-4 py-3">
          <p class="text-sm font-medium text-gray-900 dark:text-white">${this.esc(name)}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">${a.fb_ad_set_id}</p>
        </td>
        <td class="px-4 py-3">
          <span class="text-sm text-gray-600 dark:text-gray-400">${a.fb_campaign_id || "\u2014"}</span>
        </td>
        <td class="px-4 py-3">${status}</td>
      </tr>`
    })

    this.adsetsTbodyTarget.innerHTML = warningRows.concat(rows).join("")
    this.updateAdsetCount()
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  toggleAdset() { this.updateAdsetCount() }

  toggleAllAdsets(event) {
    const checked = event.target.checked
    this.adsetsTbodyTarget.querySelectorAll("input[type='checkbox']").forEach(cb => {
      if (!cb.closest("tr").classList.contains("hidden")) cb.checked = checked
    })
    this.updateAdsetCount()
  }

  filterAdsets() {
    const query = this.adsetSearchTarget.value.toLowerCase().trim()
    this.adsetsTbodyTarget.querySelectorAll("tr").forEach(row => {
      const name = row.querySelector("p")?.textContent?.toLowerCase() || ""
      row.classList.toggle("hidden", query.length > 0 && !name.includes(query))
    })
  }

  // --- Helpers ---

  getSelectedCampaignIds() {
    return Array.from(
      this.campaignsTbodyTarget.querySelectorAll("input[type='checkbox']:checked")
    ).map(cb => cb.value)
  }

  clearCampaigns() {
    if (!this.hasCampaignsTbodyTarget) return
    this.campaignsTbodyTarget.innerHTML = this.messageRow("Choose an Ad Account to view Campaigns", 4)
    this.updateCampaignCount()
  }

  clearAdsets() {
    if (!this.hasAdsetsTbodyTarget) return
    this.adsetsTbodyTarget.innerHTML = this.messageRow("Choose a Campaign to select an Ad Set", 4)
    this.updateAdsetCount()
  }

  showLoading(target) {
    target.innerHTML = `<tr>
      <td colspan="4" class="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
        <svg class="w-6 h-6 mx-auto mb-2 animate-spin text-brand" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <p>Loading...</p>
      </td>
    </tr>`
  }

  emptyStateWithRefresh(message, isSyncing = false) {
    const spinnerHtml = isSyncing ? `<svg class="w-5 h-5 mx-auto mb-2 animate-spin text-brand" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>` : ""
    return `<tr><td colspan="4" class="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
      ${spinnerHtml}
      <p class="mb-3">${this.esc(message)}</p>
      <button type="button" data-action="campaign-selector#syncCampaigns"
              class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand-dark transition-colors">
        <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>Refresh Campaigns</span>
      </button>
    </td></tr>`
  }

  messageRow(message, colspan, isError = false) {
    const color = isError ? "text-red-500 dark:text-red-400" : "text-gray-500 dark:text-gray-400"
    return `<tr><td colspan="${colspan}" class="px-4 py-8 text-center ${color}">
      <p>${this.esc(message)}</p>
    </td></tr>`
  }

  statusBadge(status) {
    const s = (status || "").toLowerCase()
    if (s === "active") return '<span class="badge-active">Active</span>'
    if (s === "paused") return '<span class="badge-paused">Paused</span>'
    const label = status ? status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) : "Unknown"
    return `<span class="badge-muted">${this.esc(label)}</span>`
  }

  updateCampaignCount() {
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent =
        this.campaignsTbodyTarget.querySelectorAll("input[type='checkbox']:checked").length
    }
  }

  updateAdsetCount() {
    if (this.hasAdsetSelectedCountTarget) {
      this.adsetSelectedCountTarget.textContent =
        this.adsetsTbodyTarget.querySelectorAll("input[type='checkbox']:checked").length
    }
  }

  esc(str) {
    const d = document.createElement("div")
    d.textContent = str
    return d.innerHTML
  }
}
